import ArrowUpIcon from '@assets/icons/arrow-up.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AddItemInlinePanel } from './inbound-detail-list-add-item-inline-panel';
import { Analytics } from '@/utils/analytics';
import { CorrectionItemInlinePanel } from './inbound-detail-list-correction-item-inline-panel';
import {
  CorrectionPreviewMap,
  InboundListItem,
  OrderAgentWorkflowMode,
  OrderMetadata,
  SwapSuggestionMap,
  VariantPackOrderCountsMap,
} from './types';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeRestaurant } from '@/contexts/user-context';
import { getRememberSelectionFromItem } from './utils';
import { InboundDetailListCustomerAccountPicker } from './inbound-detail-list-customer-account-picker';
import {
  InboundDetailListItem,
  MIN_POPOVER_HEIGHT,
  PendingRemovalData,
} from './inbound-detail-list-item';
import {
  InboundDetailListOrderOptions,
  InboundDetailListOrderOptionsRef,
} from './inbound-detail-list-order-options';
import { InboundDetailSummaryRow } from './inbound-detail-summary-row';
import {
  InboundReducerAction,
  InputToQuantityMap,
  MatchTerm,
  OrderInboundState,
} from '@/hooks/use-order-inbound-reducer';
import {
  InteractionManager,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleProp,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { LIST_ITEM_GAP } from '@/hooks/use-order-inbounds-detail';
import { ListRenderItemInfo } from '@shopify/flash-list';
import { ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import { OrderInboundsStackParams } from '@/navigators/types';
import { PillNav } from './pill-nav';
import { RecyclerList } from '@/components/recycler-list';
import { RemoveListItemActionSheet } from './remove-list-item-action-sheet';
import { SCREEN_PADDING } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { SwapItemInlinePanel } from './inbound-detail-list-swap-item-inline-panel';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useInboundOrderSummary } from './hooks/use-inbound-order-summary';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { usePillNav } from './hooks/use-pill-nav';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useThreadOrderEventsContext } from './thread-order-events-context';

interface OrderOptionsItem {
  isSpecialItem: true;
  title: '';
  value: '__ORDER_OPTIONS__';
}

interface OrderSummaryItem {
  isSpecialItem: true;
  title: '';
  value: '__ORDER_SUMMARY__';
}

interface PillNavItem {
  isSpecialItem: true;
  title: '';
  value: '__PILL_NAV__';
}

type InboundDetailListData =
  | InboundListItem
  | OrderOptionsItem
  | OrderSummaryItem
  | PillNavItem;

function isOrderOptionsItem(
  item: InboundDetailListData,
): item is OrderOptionsItem {
  return item.value === '__ORDER_OPTIONS__';
}

function isOrderSummaryItem(
  item: InboundDetailListData,
): item is OrderSummaryItem {
  return item.value === '__ORDER_SUMMARY__';
}

function isPillNavItem(item: InboundDetailListData): item is PillNavItem {
  return item.value === '__PILL_NAV__';
}

export interface Props {
  activeRestaurant: EmployeeRestaurant | null;
  addItemSmartOrderUUID: string;
  alternativeVariantPacksMap: SwapSuggestionMap;
  containerStyle?: StyleProp<ViewStyle>;
  correctionPreviewMap: CorrectionPreviewMap;
  dispatch: React.Dispatch<InboundReducerAction>;
  inDraggableSheet?: boolean;
  isProcessing?: boolean;
  listItems: InboundListItem[];
  matchTerms: MatchTerm[];
  onInteraction?: () => void;
  onItemCorrection: (
    item: InboundListItem,
    nextItem: ItemListOptionMetadata,
    onComplete?: () => void,
  ) => Promise<void>;
  onListScroll?: (
    event: NativeSyntheticEvent<NativeScrollEvent>,
    scrollDelta: { x: number; y: number },
  ) => void;
  onRemoveItem: (itemToDelete: InboundListItem) => void;
  onSetRememberSelection: (
    item: InboundListItem,
    rememberSelection: boolean,
  ) => Promise<void>;
  onSwapItem: (
    previousItem: InboundListItem,
    nextItem: ItemListOptionMetadata,
    onComplete?: () => void,
    rememberSelection?: boolean,
  ) => Promise<void>;
  orderInboundState: OrderInboundState;
  orderMetadata: OrderMetadata[];
  orderOptionsRef?: React.RefObject<InboundDetailListOrderOptionsRef | null>;
  orderUUIDs: string[];
  pillNavProps: ReturnType<typeof usePillNav>;
  previousVariantPacksMap: SwapSuggestionMap;
  savedVariantPackOrderCountsMap: VariantPackOrderCountsMap;
  searchInputSubstitutionsMap: SwapSuggestionMap;
  searchInputToQuantityMap: InputToQuantityMap;
  showItemListView: boolean;
  // TODO Refactor list item component so this is determined based off width
  showLocationPicker?: () => void;
  showTranslation?: boolean;
  updateItemNotes: (searchInputUUID: string, itemNotes: string | null) => void;
  updateQuantity: (searchInputUUID: string, quantity: number) => void;
  workflowMode: OrderAgentWorkflowMode;
}

const MAX_ITEM_WIDTH = 400;
const LIST_FOOTER_OFFSET = 416;
const SCROLL_TOP_THRESHOLD = 100;

export const InboundDetailList: React.FC<Props> = ({
  activeRestaurant,
  addItemSmartOrderUUID,
  alternativeVariantPacksMap,
  containerStyle,
  correctionPreviewMap,
  dispatch,
  inDraggableSheet,
  isProcessing,
  listItems,
  matchTerms,
  onInteraction,
  onItemCorrection,
  onRemoveItem,
  onSetRememberSelection,
  onSwapItem,
  orderMetadata,
  orderOptionsRef,
  orderUUIDs,
  pillNavProps: {
    getPillNavRefHandler,
    handlePillNavContentScroll,
    handlePillNavContentScrollEnd,
    listRef,
    matchTermIndex,
    scrollViewRef,
  },
  previousVariantPacksMap,
  savedVariantPackOrderCountsMap,
  searchInputToQuantityMap,
  showItemListView,
  showLocationPicker,
  showTranslation,
  searchInputSubstitutionsMap,
  updateItemNotes,
  updateQuantity,
  onListScroll,
  workflowMode,
}) => {
  const { isSmallLayout, isSmallTabletLayout, isMediumLayout } =
    useLayoutContext();
  const { closeDetailSheet } = useItemDetailContext();
  const navigation =
    useNavigation<StackNavigationProp<OrderInboundsStackParams>>();
  const [styles, theme] = useStyles();
  const { height } = useWindowDimensions();

  const [pendingRemoval, setPendingRemoval] =
    useState<PendingRemovalData | null>(null);
  const [listWidth, setListWidth] = useState<number | null>(null);
  const carouselItemWidth = listWidth
    ? Math.min(listWidth - 24, MAX_ITEM_WIDTH)
    : 0;
  const paddingBottom = isSmallLayout
    ? 0
    : Math.max(height - LIST_FOOTER_OFFSET, MIN_POPOVER_HEIGHT);

  const { isViewOnly } = useOrderInbounds();
  const isCorrectionMode = workflowMode === OrderAgentWorkflowMode.Correction;
  const isOrderOpen = !isCorrectionMode;
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );
  const isInboundHistoryEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'inbound_history_enabled',
  );
  const { onThreadOrderProcessed } = useThreadOrderEventsContext();
  const orderSummary = useInboundOrderSummary({
    inputToQuantityMap: searchInputToQuantityMap,
    listItems,
    orderMetadata,
  });

  const itemToCorrect = useOrderInboundDetailStore(
    (state) => state.itemToCorrect,
  );
  const setItemToCorrect = useOrderInboundDetailStore(
    (state) => state.actions.setItemToCorrect,
  );
  const itemToSwap = useOrderInboundDetailStore((state) => state.itemToSwap);
  const setItemToSwap = useOrderInboundDetailStore(
    (state) => state.actions.setItemToSwap,
  );
  const showAddItemSheet = useOrderInboundDetailStore(
    (state) => state.showAddItemSheet,
  );
  const setShowAddItemSheet = useOrderInboundDetailStore(
    (state) => state.actions.setShowAddItemSheet,
  );

  const scrollPosRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const isScrolledDownRef = useRef(false);
  const isExternalItemsPendingRef = useRef(false);
  const prevItemCountRef = useRef(listItems.length);
  const [showScrollToTopButton, setShowScrollToTopButton] = useState(false);

  useEffect(() => {
    if (!isEmailThreadsEnabled) {
      return;
    }
    return onThreadOrderProcessed((event) => {
      if (event.isReply) {
        isExternalItemsPendingRef.current = true;
      }
    });
  }, [isEmailThreadsEnabled, onThreadOrderProcessed]);

  useEffect(() => {
    const prevCount = prevItemCountRef.current;
    prevItemCountRef.current = listItems.length;
    if (
      listItems.length > prevCount &&
      isScrolledDownRef.current &&
      isExternalItemsPendingRef.current
    ) {
      setShowScrollToTopButton(true);
    }
    isExternalItemsPendingRef.current = false;
  }, [listItems.length]);

  useEffect(
    () => () => {
      setShowAddItemSheet(false);
    },
    [setShowAddItemSheet],
  );

  // Function to scroll to top of the list
  const scrollToTop = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollToTop({ animated: true });
    }
  }, [listRef]);

  const headerItems = useMemo((): InboundDetailListData[] => {
    if (!isSmallLayout) {
      return [];
    }

    const items: InboundDetailListData[] = [];

    if (orderSummary.isVisible) {
      items.push({
        isSpecialItem: true,
        title: '',
        value: '__ORDER_SUMMARY__',
      });
    }

    if (matchTerms.length > 0) {
      items.push(
        {
          isSpecialItem: true,
          title: '',
          value: '__ORDER_OPTIONS__',
        },
        {
          isSpecialItem: true,
          title: '',
          value: '__PILL_NAV__',
        },
      );
    }

    return items;
  }, [isSmallLayout, matchTerms.length, orderSummary.isVisible]);

  const summaryRow = orderSummary.isVisible ? (
    <InboundDetailSummaryRow
      skuCount={orderSummary.skuCount}
      subtotalDisplay={orderSummary.subtotalDisplay}
      unitCount={orderSummary.unitCount}
    />
  ) : null;

  // Derived so the pinned row follows the pill nav rather than a hardcoded index
  const stickyHeaderIndices = useMemo(() => {
    const pillNavIndex = headerItems.findIndex(isPillNavItem);
    return pillNavIndex === -1 ? [] : [pillNavIndex];
  }, [headerItems]);

  // The summary row's list item is a stable object, so the totals it renders
  // have to reach FlashList through extraData for the recycled cell to refresh
  const listExtraData = useMemo(
    () => ({ orderSummary, searchInputToQuantityMap }),
    [orderSummary, searchInputToQuantityMap],
  );

  const sectionsWithHeaders = useMemo(() => {
    return [
      {
        data: [...headerItems, ...listItems] as InboundDetailListData[],
        key: null,
      },
    ];
  }, [headerItems, listItems]);

  const navigateToSearch = useCallback(
    (item: InboundListItem, index: number) => {
      const searchInputUUID = item.metadata?.inputWithResult.uuid;
      if (isSmallLayout) {
        navigation.navigate(ORDER_INBOUNDS_ROUTES.SwapSearch, {
          alternativeMatches: searchInputUUID
            ? alternativeVariantPacksMap.get(searchInputUUID) || []
            : undefined,
          initialRememberSelection: getRememberSelectionFromItem(item),
          initialVariantPackUUID:
            item.metadata?.listOption?.metadata?.variantPackUUID,
          onSetRememberSelection: (nextValue) => {
            onSetRememberSelection(item, nextValue);
          },
          previousMatches: searchInputUUID
            ? previousVariantPacksMap.get(searchInputUUID) || []
            : undefined,
          savedVariantPackOrderCountsMap,
          selectItem: async (metadata, remember) => {
            await onSwapItem(
              item,
              metadata,
              () => {
                navigation.goBack();
              },
              remember,
            );
          },
        });
        return;
      }
      setTimeout(() => {
        listRef.current?.scrollToIndex({
          animated: true,
          index,
        });
      });
      setItemToSwap(item);
    },
    [
      alternativeVariantPacksMap,
      isSmallLayout,
      listRef,
      navigation,
      onSetRememberSelection,
      onSwapItem,
      previousVariantPacksMap,
      savedVariantPackOrderCountsMap,
      setItemToSwap,
    ],
  );

  const openCorrectionSearch = useCallback(
    (item: InboundListItem, index: number) => {
      const searchInputUUID = item.metadata?.inputWithResult.uuid;
      const headerMessage = i18n.t('correction_flow.future_orders_header', {
        itemName: item.metadata?.listOption?.title ?? item.title,
        restaurantName: activeRestaurant?.name ?? '',
      });
      if (isSmallLayout) {
        navigation.navigate(ORDER_INBOUNDS_ROUTES.CorrectionSearch, {
          alternativeMatches: searchInputUUID
            ? alternativeVariantPacksMap.get(searchInputUUID) || []
            : undefined,
          headerMessage,
          initialVariantPackUUID:
            item.metadata?.listOption?.metadata?.variantPackUUID,
          previousMatches: searchInputUUID
            ? previousVariantPacksMap.get(searchInputUUID) || []
            : undefined,
          savedVariantPackOrderCountsMap,
          selectItem: async (metadata) => {
            await onItemCorrection(item, metadata, () => {
              navigation.goBack();
            });
          },
        });
        return;
      }
      setTimeout(() => {
        listRef.current?.scrollToIndex({
          animated: true,
          index,
        });
      });
      setItemToCorrect(item);
    },
    [
      activeRestaurant,
      alternativeVariantPacksMap,
      isSmallLayout,
      listRef,
      navigation,
      onItemCorrection,
      previousVariantPacksMap,
      savedVariantPackOrderCountsMap,
      setItemToCorrect,
    ],
  );

  const renderListItem = ({
    item,
    index,
  }: ListRenderItemInfo<InboundDetailListData>) => {
    if (isOrderOptionsItem(item)) {
      return (
        <InboundDetailListOrderOptions
          accountPickerProps={{
            disabled: isProcessing || isViewOnly,
            restaurantAddress: activeRestaurant?.addressString,
            restaurantName: activeRestaurant?.name,
            showLocationPicker: showLocationPicker,
          }}
          containerStyle={styles.fulfillmentDatePONumberContainer}
          onScrollToSelf={scrollToTop}
          orderMetadata={orderMetadata}
          ref={orderOptionsRef}
          workflowMode={workflowMode}
        />
      );
    }

    if (isOrderSummaryItem(item)) {
      return summaryRow;
    }

    if (isPillNavItem(item)) {
      return (
        <View style={styles.pillNavContainer}>
          <PillNav
            matchTermIndex={matchTermIndex}
            matchTerms={matchTerms}
            orderUUIDs={orderUUIDs}
          />
        </View>
      );
    }

    const searchInputUUID = item.metadata!.inputWithResult.uuid;
    const variantPackWithQuantities =
      searchInputToQuantityMap[searchInputUUID] || {};
    return (
      <View
        collapsable={false}
        ref={getPillNavRefHandler(item)}
        style={[
          styles.listItemWrapper,
          index === 0 ? styles.listItemWrapperFirst : null,
        ]}
      >
        {activeRestaurant ? (
          <InboundDetailListItem
            carouselItemWidth={carouselItemWidth}
            correctionPreview={correctionPreviewMap.get(searchInputUUID)}
            inboundListItem={item}
            inDraggableSheet={inDraggableSheet}
            isOrderOpen={isOrderOpen}
            listWidth={listWidth}
            navigateToCorrection={(item) => {
              openCorrectionSearch(item, index);
            }}
            navigateToSearch={(item) => {
              navigateToSearch(item, index);
            }}
            onSwapItem={(
              item: InboundListItem,
              metadata: ItemListOptionMetadata,
              onComplete?: () => void,
            ) => {
              onSwapItem(item, metadata, () => {
                onComplete?.();
              });
            }}
            popoverContent={
              itemToSwap?.value === item.value ? (
                <SwapItemInlinePanel
                  alternativeMatches={
                    alternativeVariantPacksMap.get(searchInputUUID) ?? []
                  }
                  initialRememberSelection={getRememberSelectionFromItem(
                    itemToSwap,
                  )}
                  onClose={() => setItemToSwap(null)}
                  onSetRememberSelectionCallback={(nextValue) => {
                    onSetRememberSelection(itemToSwap!, nextValue);
                  }}
                  onSwapItemCallback={async (metadata, remember) => {
                    if (itemToSwap) {
                      await onSwapItem(
                        itemToSwap!,
                        metadata,
                        () => {
                          closeDetailSheet();
                          setItemToSwap(null);
                        },
                        remember,
                      );
                    }
                  }}
                  previousMatches={
                    previousVariantPacksMap.get(searchInputUUID) ?? []
                  }
                  savedVariantPackOrderCountsMap={
                    savedVariantPackOrderCountsMap
                  }
                  substitutionsMatches={
                    searchInputSubstitutionsMap.get(searchInputUUID) ?? []
                  }
                  variantPackUUID={
                    itemToSwap?.metadata!.listOption?.metadata
                      ?.variantPackUUID ?? ''
                  }
                />
              ) : itemToCorrect?.value === item.value ? (
                <CorrectionItemInlinePanel
                  alternativeMatches={
                    alternativeVariantPacksMap.get(searchInputUUID) ?? []
                  }
                  headerMessage={i18n.t(
                    'correction_flow.future_orders_header',
                    {
                      itemName:
                        itemToCorrect.metadata?.listOption?.title ??
                        itemToCorrect.title,
                      restaurantName: activeRestaurant?.name ?? '',
                    },
                  )}
                  onClose={() => setItemToCorrect(null)}
                  onItemCorrectionCallback={async (metadata) => {
                    if (itemToCorrect) {
                      await onItemCorrection(itemToCorrect, metadata, () => {
                        setItemToCorrect(null);
                      });
                    }
                  }}
                  previousMatches={
                    previousVariantPacksMap.get(searchInputUUID) ?? []
                  }
                  savedVariantPackOrderCountsMap={
                    savedVariantPackOrderCountsMap
                  }
                  substitutionsMatches={
                    searchInputSubstitutionsMap.get(searchInputUUID) ?? []
                  }
                  variantPackUUID={
                    itemToCorrect?.metadata!.listOption?.metadata
                      ?.variantPackUUID ?? ''
                  }
                />
              ) : null
            }
            restaurantUUID={activeRestaurant.uuid}
            setPendingRemoval={setPendingRemoval}
            showItemListView={showItemListView}
            showTranslation={showTranslation}
            supplierUUID={activeRestaurant.supplierUUID}
            updateItemNotes={updateItemNotes}
            updateQuantity={updateQuantity}
            variantPackWithQuantities={variantPackWithQuantities}
            workflowMode={workflowMode}
          />
        ) : null}
      </View>
    );
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollXDelta =
      event.nativeEvent.contentOffset.x - scrollPosRef.current.x;
    const scrollYDelta =
      event.nativeEvent.contentOffset.y - scrollPosRef.current.y;

    onListScroll?.(event, { x: scrollXDelta, y: scrollYDelta });
    onInteraction?.();
    handlePillNavContentScroll(event);

    const y = event.nativeEvent.contentOffset.y;
    isScrolledDownRef.current = y > SCROLL_TOP_THRESHOLD;
    if (y <= SCROLL_TOP_THRESHOLD) {
      setShowScrollToTopButton(false);
    }

    scrollPosRef.current = { ...event.nativeEvent.contentOffset };
  };

  if (!isSmallLayout && showAddItemSheet) {
    return (
      <AddItemInlinePanel
        dispatch={dispatch}
        onClose={(hasItems) => {
          if (hasItems) {
            InteractionManager.runAfterInteractions(() =>
              listRef.current?.scrollToEnd({ animated: true }),
            );
          }
          closeDetailSheet();
          setShowAddItemSheet(false);
        }}
        smartOrderUUID={addItemSmartOrderUUID}
      />
    );
  }

  return (
    <View
      onStartShouldSetResponderCapture={() => {
        onInteraction?.();
        return false;
      }}
      style={styles.listContainer}
    >
      <RecyclerList<InboundDetailListData>
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' ? styles.contentContainerWeb : null,
          containerStyle,
          isSmallTabletLayout ? styles.contentContainerTablet : null,
          isMediumLayout ? styles.contentContainerMedium : null,
          orderSummary.isVisible ? styles.contentContainerWithSummary : null,
        ]}
        estimatedItemSize={100}
        extraData={listExtraData}
        ListFooterComponentStyle={{ paddingBottom }}
        ListHeaderComponent={
          !isSmallLayout ? (
            <View style={styles.listHeaderContainer}>
              {summaryRow}

              <View style={styles.listHeaderComponentContainer}>
                <InboundDetailListCustomerAccountPicker
                  disabled={isProcessing || isViewOnly || isCorrectionMode}
                  restaurantAddress={activeRestaurant?.addressString}
                  restaurantName={activeRestaurant?.name}
                  showLocationPicker={showLocationPicker}
                />

                <InboundDetailListOrderOptions
                  onScrollToSelf={scrollToTop}
                  orderMetadata={orderMetadata}
                  ref={orderOptionsRef}
                  workflowMode={workflowMode}
                />
              </View>
            </View>
          ) : null
        }
        listRef={listRef}
        onLayout={(e) => {
          setListWidth(e.nativeEvent.layout.width);
        }}
        onMomentumScrollEnd={handlePillNavContentScrollEnd}
        onScroll={onScroll}
        renderItem={renderListItem}
        scrollEnabled={!itemToSwap && !itemToCorrect}
        scrollViewRef={scrollViewRef}
        sections={sectionsWithHeaders}
        shouldRenderSeparator={(leadingItem) => {
          if (isInboundHistoryEnabled) {
            return false;
          }

          if (itemToSwap && leadingItem.value === itemToSwap.value) {
            return false;
          }

          return !itemToCorrect || leadingItem.value !== itemToCorrect.value;
        }}
        skipInteractionCheck
        stickyHeaderIndices={stickyHeaderIndices}
        testID="inbound-detail-list"
      />

      {pendingRemoval && activeRestaurant ? (
        <RemoveListItemActionSheet
          activeRestaurant={activeRestaurant}
          itemToRemove={pendingRemoval.itemToRemove}
          onClose={(itemRemoved) => {
            if (itemRemoved) {
              onRemoveItem(pendingRemoval.itemToRemove);
            } else {
              // restore item by resetting the quantity from 0 to previous quantity
              const searchInputUUID =
                pendingRemoval.itemToRemove.metadata!.inputWithResult.uuid;
              updateQuantity(
                searchInputUUID,
                pendingRemoval.updateQuantityData.previousQuantity,
              );
            }
            setPendingRemoval(null);
          }}
        />
      ) : null}
      {showScrollToTopButton ? (
        <View style={styles.scrollToTopButtonContainer}>
          <Pressable
            onPress={() => {
              scrollToTop();
              setShowScrollToTopButton(false);
              Analytics.track(
                'order_inbounds.detail_list.scroll_to_top.press',
                {
                  orderUUIDs,
                },
              );
            }}
            style={styles.scrollToTopButton}
          >
            <View style={styles.scrollToTopButtonInner}>
              <ArrowUpIcon
                fill={theme.sk.color.icon.ondark}
                height={16}
                width={16}
              />

              <Typography
                color={theme.sk.color.text.ondark}
                variant="paragraph-md-600"
              >
                {i18n.t('toast.new_items_added')}
              </Typography>
            </View>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  contentContainer: {
    backgroundColor: theme.colors.white,
    paddingBottom: SCREEN_PADDING,
    paddingTop: SCREEN_PADDING,
  },
  contentContainerMedium: {
    paddingBottom: 60,
  },
  contentContainerTablet: {
    paddingBottom: 100,
  },
  contentContainerWeb: {
    paddingBottom: 0,
  },
  contentContainerWithSummary: {
    paddingTop: 0,
  },
  fulfillmentDatePONumberContainer: {
    paddingHorizontal: 16,
  },
  listContainer: {
    flex: 1,
  },
  listHeaderComponentContainer: {
    gap: 16,
    paddingHorizontal: 16,
  },
  listHeaderContainer: {
    gap: 16,
  },
  listItemWrapper: {
    marginTop: LIST_ITEM_GAP,
  },
  listItemWrapperFirst: {
    marginTop: 0,
  },
  pillNavContainer: {
    backgroundColor: theme.colors.white,
  },
  scrollToTopButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  scrollToTopButtonContainer: {
    alignItems: 'center',
    position: 'absolute',
    top: 16,
    width: '100%',
    zIndex: 10,
  },
  scrollToTopButtonInner: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
  },
}));
